import { TacticalNode } from "@/core/battle/ai/types/ai.types";
import { AIWeights } from "@/core/battle/ai/utils/AIConfig";
import { CombatCalculator } from "@/core/battle/CombatCalculator";
import { BattleUnit, Skill } from "@/core/types/battle.types";
import { getUnitTotalHp } from "@/core/utils/battle.utils";
import { coordsToId } from "@/core/utils/hex.utils";

export class EvaluatorUtils {

    static scoreHex(
        unit: BattleUnit,
        dist: number,
        moveCost: number,
        stats: { minDist: number; maxDist: number },
        rawThreat: number,
        alliesCount: number,
    ): { proximity: number, safety: number } {
        const optimalDist = unit.skills[0]?.range || 1;
        // const effectiveDist = (optimalDist > 1 && dist < optimalDist) ? moveCost : moveCost;
        const penalty = (optimalDist > 1 && dist < optimalDist) ? (optimalDist - dist) / optimalDist : 0;
        const proximity = Math.max(0, 1 - (moveCost - stats.minDist) / (stats.maxDist - stats.minDist) - penalty);
        
        // const proximity = 1 - (effectiveDist - stats.minDist) / (stats.maxDist - stats.minDist);
        const exponent = 0.5; 
        const virtualHp = unit.maxUnitHp * Math.pow(getUnitTotalHp(unit) / unit.maxUnitHp, exponent);
        const divider = Math.max(1, alliesCount - 1 / 2);
        // const lethality = Math.min(1.0, (rawThreat / divider) / virtualHp);
        const lethality = Math.min(1.0, rawThreat / virtualHp);
        // const safety = Math.max(ctx.weights.MIN_SAFETY, 1.0 - lethality);
        // const lethality = Math.min(1.0, rawThreat / getUnitTotalHp(unit));
        const safety = 1.0 - lethality;

        return { proximity, safety };
    }
    
    static threatZone(
        pos: { q: number; r: number },
        threatMap: Map<string, number>,
        stackHP: number,
        CV: number,
        weights: AIWeights
    ): number {
        const rawThreat = threatMap.get(coordsToId(pos)) || 0;
        if (rawThreat <= 0) return 0;

        // 1. Lethality Ratio (0.0 до 1.0)
        // Скільки % від нашого ПОТОЧНОГО здоров'я знесе ворог за цей хід
        // Використовуємо Math.min, щоб смерть не важила "більше ніж смерть"
        const lethality = Math.min(1.0, rawThreat / stackHP);

        // 2. Розрахунок штрафу
        // Ми НЕ ділимо тут на CV, бо це "чисте" значення 0-1 (відсоток загрози)
        // Ми просто множимо на вагу. 
        // CV застосуємо в самому кінці, коли будемо сумувати з іншими компонентами
        // const riskPenalty = lethality * weights.THREAT_ZONE_WEIGHT;
        const riskPenalty = Math.pow(lethality, 2) * weights.THREAT_ZONE_WEIGHT;

        // Повертаємо негативне значення
        return -riskPenalty;
    }

    static calculateAvgDmg(unit: BattleUnit, perTurn: boolean = true): number {
        let attacksPerTurn = 1;
        if (perTurn) {
            const skill = unit.skills[0];
            const skillCost = skill?.cost || 40;
            attacksPerTurn = Math.floor(unit.stats.stamina / skillCost);
        }
        return ((unit.stats.damageMin + unit.stats.damageMax) / 2) * Math.max(1, attacksPerTurn);
    }

    static calculateOffensivePower(unit: BattleUnit): number {
        const s = unit.stats;
        const avgDmg = this.calculateAvgDmg(unit);
        const ap = (s.armorPenetration || 0) + (unit.skills[0]?.armorPenBonus || 0);
        
        const rangeBonus = 1 + Math.log10(unit.skills[0]?.range || 1) * 2;
        
        // const offensivePower = avgDmg * (1 + ap) * CombatCalculator.getEffectiveCount(unit.count) * rangeBonus;
        return avgDmg * (1 + ap) * CombatCalculator.getEffectiveCount(unit.count) * rangeBonus;
    }

    static calculateStackPower(unit: BattleUnit): number {
        const evasionFactor = 1 + (unit.stats.evasion / 100);
        const durability = getUnitTotalHp(unit) * (1 + unit.stats.armor / 100) * evasionFactor;
        
        const offensivePower = this.calculateOffensivePower(unit);
        
        // Нова логіка ваги: 0.4 для захисту, 0.6 для атаки
        const basePower = Math.pow(durability, 0.45) * Math.pow(offensivePower, 0.55);
        
        // В умовах швидкого TTK ініціатива — це все. Збільшуємо бонус.
        const initiativeBonus = unit.stats.initiative * 0.002; // Було 0.002
        const speedBonus = unit.stats.speed * 0.015;
        const tacticalBonus = 1 + speedBonus + initiativeBonus;
        // console.log('calculateStackPower', unit.unitId, basePower, tacticalBonus, durability, offensivePower)
        return basePower * tacticalBonus;
    }
}

export class PriorityQueue<T> {
    private heap: Array<{ element: T; priority: number }> = [];

    public isEmpty(): boolean {
        return this.heap.length === 0;
    }

    public push(element: T, priority: number): void {
        this.heap.push({ element, priority });
        this.bubbleUp(this.heap.length - 1);
    }

    public pop(): { currentId: T; currentCost: number } | undefined {
        if (this.isEmpty()) return undefined;
        
        const top = this.heap[0];
        const last = this.heap.pop();

        if (!top) return undefined;
        if (last === undefined) return undefined;
        
        if (this.heap.length > 0) {
            this.heap[0] = last;
            this.bubbleDown(0);
        }
        
        return { currentId: top.element, currentCost: top.priority };
    }

    private bubbleUp(index: number): void {
        while (index > 0) {
            const parentIndex = Math.floor((index - 1) / 2);
            
            const current = this.heap[index];
            const parent = this.heap[parentIndex];

            // Перевірка на існування для TS
            if (!current || !parent) break;
            if (parent.priority <= current.priority) break;
            
            this.swap(index, parentIndex);
            index = parentIndex;
        }
    }

    private bubbleDown(index: number): void {
        const length = this.heap.length;

        while (true) {
            const leftChildIdx = 2 * index + 1;
            const rightChildIdx = 2 * index + 2;
            let swapIdx: number | null = null;

            const current = this.heap[index];
            if (!current) break;

            if (leftChildIdx < length) {
                const leftChild = this.heap[leftChildIdx];
                if (leftChild && leftChild.priority < current.priority) {
                    swapIdx = leftChildIdx;
                }
            }

            if (rightChildIdx < length) {
                const rightChild = this.heap[rightChildIdx];
                const comparisonNode = swapIdx === null ? current : this.heap[swapIdx];
                
                if (rightChild && comparisonNode && rightChild.priority < comparisonNode.priority) {
                    swapIdx = rightChildIdx;
                }
            }

            if (swapIdx === null) break;
            
            this.swap(index, swapIdx);
            index = swapIdx;
        }
    }

    private swap(i: number, j: number): void {
        const temp = this.heap[i];
        const target = this.heap[j];
        if (temp && target) {
            this.heap[i] = target;
            this.heap[j] = temp;
        }
    }
}